import classNames from 'classnames';
import React from 'react';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import arrowUpRightIcon from '../icons/non-prefilled/16x16/arrowUpRight.svg';
import getFirstName from '../utils/text/getFirstName';
import * as styles from './ActivityItem.module.scss';

const ACTIVITY_ITEM_ACTORS_FRAGMENT = gql(/** @complexity 25 */ `
  fragment ActivityItem_Actors on User {
    id
    ...AvatarStack_users
  }
`);

type ActivityItemProps = {
  icon: string | React.ReactNode;
  description?: React.ReactNode;
  timestamp?: string | number;
  actors?: DocumentType<typeof ACTIVITY_ITEM_ACTORS_FRAGMENT>[];
  onClick?: () => void;
  href?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  linkArrow?: 'auto' | 'always' | 'never';
  'aria-posinset'?: number;
};

const ActivityItem = ({
  actors,
  icon,
  description,
  timestamp,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
  linkArrow = 'auto',
  'aria-posinset': ariaPosinset,
}: ActivityItemProps) => {
  const hasTwoRows = !!timestamp || !!actors?.length;

  const isInteractive = !!onClick || !!href;
  const hasLinkArrow = linkArrow === 'always' || (linkArrow === 'auto' && href);
  const Component = isInteractive ? Button : 'div';
  return (
    <article
      aria-posinset={ariaPosinset}
      aria-setsize={ariaPosinset == null ? undefined : -1}
      className={classNames(styles.activityItem, {
        [styles.isInteractive]: isInteractive,
      })}
    >
      <Component
        className={styles.component}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        to={href}
      >
        <div className={styles.activityInner}>
          <div className={styles.hoverIndicator} />
          <div className={styles.activityVerticalLine} />
          <div className={styles.activityIcon}>
            {typeof icon === 'string' ? (
              <SVGIcon size={16}>{icon}</SVGIcon>
            ) : (
              icon
            )}
          </div>
          <div className={styles.activityFirstRow}>
            {!hasTwoRows && description}
            {hasTwoRows && !!actors?.length && (
              <>
                <span className={styles.activityAvatars}>
                  <AvatarStack
                    items={actors}
                    noWrap
                    size={20}
                    limit={5}
                    countOverflowVariant="circle"
                    spacing="tight"
                    borderSize={2}
                  />
                </span>
                <span className={styles.activityNames}>
                  {actors.map((actor, index) => (
                    <React.Fragment key={actor.id}>
                      {(() => {
                        if (index === 0) return '';
                        if (index === actors.length - 1) return ' and ';
                        return ', ';
                      })()}
                      <span className={styles.actorName}>
                        {getFirstName(actor.name)}
                      </span>
                    </React.Fragment>
                  ))}
                </span>
              </>
            )}
            {hasTwoRows && !!timestamp && (
              <span className={styles.activityTime}>
                <Timestamp date={timestamp} format="short" />
              </span>
            )}
            {hasLinkArrow && (
              <SVGIcon className={styles.activityLinkArrow}>
                {arrowUpRightIcon}
              </SVGIcon>
            )}
          </div>
          {hasTwoRows && (
            <div className={styles.activitySecondRow}>{description}</div>
          )}
        </div>
      </Component>
    </article>
  );
};

interface ActivitySeparatorProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const ActivitySeparator = ({
  children,
  onClick,
}: ActivitySeparatorProps) => {
  return (
    <ActivityItem
      icon={<div className={styles.activitySeparatorIcon} />}
      description={
        <div className={styles.activitySeparatorText}>{children}</div>
      }
      onClick={onClick}
    />
  );
};

export default ActivityItem;
